import React, { useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm, FormProvider } from 'react-hook-form'
import { memo } from 'react'
import { useTheme } from '@mui/material'

import {
  CLIENT_SIDE_CHECKBOX_FIELD_NAME,
  FILTER_MODES,
} from '../../../../../components/shared/table/table.constants'
import { capitalizeFirstLetter } from '@hapana/shared/utils/string.util'
import CheckboxGroup from '../../../../../components/shared/checkbox-group/checkbox-group.component'

import { styles } from './../../table.styles'

const CheckboxGroupFilter = ({
  column: { preFilteredRows, id, filterValue, serverSideFilterOptions },
  setFilter,
  filterMode,
  shouldClearFilters,
  setShouldClearFilters,
  disabled,
}) => {
  const theme = useTheme()

  const options = useMemo(() => {
    const newOptions = []
    if (filterMode === FILTER_MODES.CLIENT_SIDE) {
      preFilteredRows.forEach((row) => {
        if (newOptions.find((option) => option.value === row.values[id])) return
        newOptions.push({
          value: row.values[id],
          label: capitalizeFirstLetter(row.values[id]),
        })
      })
      return newOptions
    } else {
      return serverSideFilterOptions || []
    }
  }, [id, preFilteredRows, filterMode, serverSideFilterOptions])

  const fieldName = `${CLIENT_SIDE_CHECKBOX_FIELD_NAME}-${id}`

  const formMethods = useForm({
    defaultValues: {
      [fieldName]: Array.isArray(filterValue) ? filterValue : [],
    },
  })

  const filteredValues = formMethods.watch(fieldName)
  const { setValue } = formMethods

  useEffect(() => {
    setFilter(id, filteredValues.length ? filteredValues : undefined)
  }, [filteredValues, setFilter, id])

  useEffect(() => {
    if (filterValue === undefined) {
      setValue(fieldName, [])
    }
  }, [filterValue, setValue, fieldName])

  useEffect(() => {
    if (shouldClearFilters) {
      setValue(fieldName, [])
      setShouldClearFilters(false)
    }
  }, [shouldClearFilters, setValue, fieldName, setShouldClearFilters])

  return (
    <FormProvider {...formMethods}>
      <form style={styles.tableForm(theme)}>
        <CheckboxGroup
          name={fieldName}
          options={options}
          optionsStyles={styles.optionsCheckbox}
          disabled={disabled}
        />
      </form>
    </FormProvider>
  )
}

CheckboxGroupFilter.propTypes = {
  column: PropTypes.shape({
    filterValue: PropTypes.array,
    preFilteredRows: PropTypes.array,
    id: PropTypes.string,
    render: PropTypes.func,
    serverSideFilterOptions: PropTypes.array,
    hasCutomFilterHeader: PropTypes.bool,
  }),
  setFilter: PropTypes.func,
  filterMode: PropTypes.oneOf(Object.values(FILTER_MODES)),
  shouldClearFilters: PropTypes.bool,
  setShouldClearFilters: PropTypes.func,
  disabled: PropTypes.bool,
}

function areEqual(prevProps, nextProps) {
  return (
    prevProps.column.serverSideFilterOptions === nextProps.column.serverSideFilterOptions &&
    prevProps.column.filterValue === nextProps.column.filterValue &&
    prevProps.shouldClearFilters === nextProps.shouldClearFilters
  )
}

export default memo(CheckboxGroupFilter, areEqual)
